import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  OnInit,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import {
  TuiButtonModule,
  TuiDialogContext,
  TuiDialogService,
  TuiGroupModule,
  TuiSvgModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import {
  TuiInputModule,
  TuiInputNumberModule,
  TuiToggleModule,
} from '@taiga-ui/kit';
import { TuiLetModule, TuiSafeHtml } from '@taiga-ui/cdk';
import { TuiTableModule } from '@taiga-ui/addon-table';
import {
  BehaviorSubject,
  catchError,
  filter,
  map,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  POLYMORPHEUS_CONTEXT,
  PolymorpheusComponent,
} from '@tinkoff/ng-polymorpheus';
import { AlertService } from '@shared/services/alert.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AutocompleteComponent } from '@shared/components/forms/autocomplete/autocomplete.component';
import { ClientService } from '@shared/services/client.service';
import { joinLatLong, splitLatLong } from '@shared/utils/coords';
import {
  LocationSelectComponent,
  LocationSelectInputData,
  LocationSelectOutputData,
} from '@shared/components/google-map/location-select/location-select.component';
import { CheckboxComponent } from '@shared/components/forms/checkbox/checkbox.component';
import { MaskitoModule } from '@maskito/angular';
import { phoneMask } from '@shared/utils/masks';
import { DomSanitizer } from '@angular/platform-browser';
import { TELEGRAM_ICON } from '@shared/components/catalogs/client-form/telegram';
import { FormsService } from '@shared/services/forms.service';
import { OrganizationMemberComponent } from '@shared/components/catalogs/organization-member/organization-member.component';
import {
  Client,
  ClientWorkConditionForm,
  DeliveryPayment,
  DocumentPlace,
  StorageKeeper,
} from '../../../../types/client';
import { TransformToFormControl } from '../../../../types/common';
import {
  OrganizationMemberContactForm,
  OrganizationMemberForm,
} from '../../../../types/organization-member';

export type ClientFormInputData = {
  client?: Client;
};

export type CoordsForm = {
  address: FormControl<Nullable<string>>;
  latLong: FormControl<Nullable<string>>;
};

type StorageKeeperForm = TransformToFormControl<StorageKeeper>;

type ClientForm = TransformToFormControl<
  Omit<
    Client,
    | 'addressesWithCoordinatesJson'
    | 'storageKeepersJson'
    | 'members'
    | 'workCondition'
  >
> & {
  addressesWithCoordinatesJson: FormArray<FormGroup<CoordsForm>>;
  storageKeepersJson: FormArray<FormGroup<StorageKeeperForm>>;
  workCondition: FormGroup<ClientWorkConditionForm>;
  members: FormArray<FormGroup<OrganizationMemberForm>>;
};

@Component({
  selector: 'app-client-form',
  standalone: true,
  imports: [
    AsyncPipe,
    TuiButtonModule,
    TuiInputModule,
    TuiLetModule,
    TuiTableModule,
    AutocompleteComponent,
    TuiTextfieldControllerModule,
    TuiToggleModule,
    ReactiveFormsModule,
    TuiGroupModule,
    TuiInputNumberModule,
    CheckboxComponent,
    MaskitoModule,
    TuiSvgModule,
    OrganizationMemberComponent,
  ],
  templateUrl: './client-form.component.html',
  styleUrl: './client-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientFormComponent implements OnInit {
  isEdit$ = new BehaviorSubject(false);
  title$ = this.isEdit$.pipe(
    map(value => (value ? 'Редактировать' : 'Создать').concat(' клиента')),
  );

  tgIconSource: TuiSafeHtml =
    this.sanitizer.bypassSecurityTrustHtml(TELEGRAM_ICON);

  selectLocation$ = new Subject<number>();

  protected readonly phoneMask = phoneMask;

  save$ = new Subject<void>();
  clientForm = this.fb.group<ClientForm>({
    id: this.fb.control<Nullable<number>>(null),
    uniqueKey: this.fb.control(''),
    name: this.fb.control<Nullable<string>>(null, Validators.required),
    inn: this.fb.control<Nullable<string>>(null),
    addressesWithCoordinatesJson: this.fb.array<FormGroup<CoordsForm>>([]),
    discount: this.fb.control<Nullable<number>>(null),
    publicAccess: this.fb.control<Nullable<boolean>>(null),
    storageKeepersJson: this.fb.array<FormGroup<StorageKeeperForm>>([]),
    tgBotChatId: this.fb.control(null),
    members: this.fb.array<FormGroup<OrganizationMemberForm>>([]),
    comment: this.fb.control(null),
    workCondition: this.forms.getWorkConditionForm(),
  });

  documentPlaces = [
    {
      text: 'Склад',
      value: DocumentPlace.STORAGE,
    },
    {
      text: 'Лоб',
      value: DocumentPlace.LOB,
    },
    {
      text: 'Нет',
      value: DocumentPlace.NONE,
    },
  ].map(i => ({ ...i, itemSearch: `${i.text}-${i.value}` }));

  deliveryPaymentTypes = [
    {
      text: '0%',
      value: DeliveryPayment.ZERO_PERCENT,
    },
    {
      text: '100%',
      value: DeliveryPayment.HUNDRED_PERCENT,
    },
    {
      text: 'Комб.',
      value: DeliveryPayment.COMB,
    },
  ].map(i => ({ ...i, itemSearch: `${i.text}-${i.value}` }));

  constructor(
    @Inject(POLYMORPHEUS_CONTEXT)
    protected readonly context: TuiDialogContext<void, ClientFormInputData>,
    private readonly fb: FormBuilder,
    private readonly destroyRef: DestroyRef,
    private readonly alert: AlertService,
    private readonly clientService: ClientService,
    private readonly dialogs: TuiDialogService,
    private readonly sanitizer: DomSanitizer,
    private readonly forms: FormsService,
  ) {}

  onSaveClick() {
    this.save$
      .pipe(
        filter(() => this.clientForm.valid),
        switchMap(() => {
          const value = this.clientForm.getRawValue();
          const client: Client = {
            ...value,
            addressesWithCoordinatesJson:
              value.addressesWithCoordinatesJson.map(({ address, latLong }) => {
                return {
                  address,
                  ...splitLatLong(latLong),
                };
              }),
          };
          return this.clientService.save(client);
        }),
        tap(() => this.closeOnSave()),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onLocationSelect() {
    this.selectLocation$
      .pipe(
        switchMap(index => {
          const data: LocationSelectInputData = {
            coords:
              this.clientForm.value.addressesWithCoordinatesJson?.[index]
                ?.latLong,
          };
          return this.dialogs
            .open<LocationSelectOutputData>(
              new PolymorpheusComponent(LocationSelectComponent),
              {
                size: 'auto',
                data,
              },
            )
            .pipe(
              tap(({ latLng, address }) => {
                const latLong = joinLatLong(
                  latLng.lat.toString(),
                  latLng.lng.toString(),
                );
                const row =
                  this.clientForm.controls.addressesWithCoordinatesJson.at(
                    index,
                  );
                row.patchValue({ address, latLong });
              }),
            );
        }),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  closeOnSave() {
    this.context.$implicit.next();
    this.context.$implicit.complete();
  }

  addAddress() {
    this.clientForm.controls.addressesWithCoordinatesJson.push(
      this.fb.group({
        latLong: this.fb.control('', Validators.required),
        address: '',
      }),
    );
  }

  removeAddress(index: number) {
    this.clientForm.controls.addressesWithCoordinatesJson.removeAt(index);
  }

  addStorageKeeper() {
    this.clientForm.controls.storageKeepersJson.push(
      this.fb.group({
        name: this.fb.control(''),
        phone: this.fb.control(''),
      }),
    );
  }

  removeMember(index: number) {
    this.clientForm.controls.members.removeAt(index);
  }

  createMemberContactForm() {
    return this.fb.group<OrganizationMemberContactForm>({
      type: this.fb.control(null),
      value: this.fb.control(null),
    });
  }

  createMemberForm() {
    return this.fb.group<OrganizationMemberForm>({
      name: this.fb.control(null),
      position: this.fb.control(null),
      contacts: this.fb.array<FormGroup<OrganizationMemberContactForm>>([]),
    });
  }

  addMember() {
    const form = this.createMemberForm();
    form.controls.contacts.push(this.createMemberContactForm());
    this.clientForm.controls.members.push(form);
  }

  removeStorageKeeper(index: number) {
    this.clientForm.controls.storageKeepersJson.removeAt(index);
  }

  setForm() {
    const client = this.context.data?.client;
    if (!client) {
      return;
    }
    const {
      addressesWithCoordinatesJson,
      storageKeepersJson,
      members,
      ...rest
    } = client;
    this.clientForm.patchValue(rest);
    this.clientForm.setControl(
      'storageKeepersJson',
      this.fb.array(storageKeepersJson.map(keeper => this.fb.group(keeper))),
    );
    this.clientForm.setControl(
      'members',
      this.fb.array(
        members.map(({ contacts, ...member }) => {
          const form = this.createMemberForm();
          form.patchValue(member);
          form.setControl(
            'contacts',
            this.fb.array(contacts.map(c => this.fb.group(c))),
          );
          return form;
        }),
      ),
    );
    this.clientForm.setControl(
      'addressesWithCoordinatesJson',
      this.fb.array(
        addressesWithCoordinatesJson.map(coords =>
          this.fb.group<CoordsForm>({
            address: this.fb.control(coords.address),
            latLong: this.fb.control(
              joinLatLong(coords.latitude, coords.longitude),
              Validators.required,
            ),
          }),
        ),
      ),
    );
  }

  ngOnInit() {
    this.onLocationSelect();
    const isEdit = !!this.context.data?.client;
    this.isEdit$.next(isEdit);
    if (isEdit) {
      this.setForm();
    }
    this.onSaveClick();
  }
}
